import React from 'react';
import { X, ChevronLeft, ChevronRight, CheckCircle, Play, SkipForward } from 'lucide-react';
import { getStartingAngleConfig } from '@/config/initializationConfig';

interface InitializationMessageProps {
  message: string;
  steps: string[];
  currentStep: number;
  startingAngle: string;
  onDismiss: () => void;
  onSkip?: () => void;
  onNextStep: () => void;
  onPreviousStep: () => void;
  onStartGuidedTour?: () => void;
}

export const InitializationMessage: React.FC<InitializationMessageProps> = ({
  message,
  steps,
  currentStep,
  onDismiss,
  onSkip,
  onNextStep,
  onPreviousStep,
  startingAngle,
  onStartGuidedTour
}) => {
  const config = getStartingAngleConfig(startingAngle);
  const handleStartGuidedTour = () => {
    if (onStartGuidedTour) {
      onStartGuidedTour();
    }
  };

  const getStartingAngleDisplayName = (angle: string) => {
    return getStartingAngleConfig(angle).displayName;
  };

  const getStartingAngleColor = (angle: string) => {
    return getStartingAngleConfig(angle).color;
  };

  const getProgressWidth = () => {
    const percentage = ((currentStep + 1) / steps.length) * 100;
    if (percentage <= 25) return 'w-1/4';
    if (percentage <= 50) return 'w-1/2';
    if (percentage <= 75) return 'w-3/4';
    return 'w-full';
  }; return (
    <div
      className="fixed top-20 right-4 z-50 w-96 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg"
      id="initialization-message-container"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <div className={`w-3 h-3 rounded-full ${getStartingAngleColor(startingAngle)}`}></div>
          <h3 className="font-semibold text-gray-900 dark:text-white">
            {getStartingAngleDisplayName(startingAngle)} Setup
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          {onSkip && (
            <button
              onClick={onSkip}
              className="flex items-center space-x-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
              title="Skip setup and mark as complete"
              aria-label="Skip project setup"
            >
              <SkipForward size={14} />
              <span>Skip Setup</span>
            </button>
          )}
          <button
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            title="Dismiss initialization guide"
            aria-label="Dismiss initialization guide"
          >
            <X size={20} />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
          {message}
        </p>

        {/* Steps */}
        <div className="space-y-3">
          <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
            <span>Getting Started Steps</span>
            <span>{currentStep + 1} of {steps.length}</span>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
            <div className="flex items-start space-x-2">
              <CheckCircle
                size={16}
                className={`mt-0.5 ${currentStep >= 0 ? 'text-green-500' : 'text-gray-300'}`}
              />
              <span className="text-sm text-gray-700 dark:text-gray-300">
                {steps[currentStep]}
              </span>
            </div>
          </div>          {/* Progress bar */}
          <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
            <div
              className={`h-1.5 rounded-full transition-all duration-300 ${getStartingAngleColor(startingAngle)} ${getProgressWidth()}`}
            ></div>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <button
              onClick={onPreviousStep}
              disabled={currentStep === 0}
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={16} />
              <span>Previous</span>
            </button>

            <button
              onClick={onNextStep}
              disabled={currentStep === steps.length - 1}
              className="flex items-center space-x-1 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <span>Next</span>
              <ChevronRight size={16} />
            </button>
          </div>        </div>        {/* Guided Tour Button */}
        {config.tourEnabled && (
          <div className="mt-3">            <button
            onClick={handleStartGuidedTour}
            className="w-full flex items-center justify-center space-x-2 px-3 py-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium rounded-lg transition-colors"
          >
            <Play size={16} />
            <span>Start Interactive Guide</span>
          </button>
          </div>
        )}

        {/* Additional tips based on starting angle */}
        <div className="mt-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
          <div className="text-xs text-blue-600 dark:text-blue-400 font-medium mb-1">
            💡 Project Tip
          </div>          <div className="text-xs text-blue-700 dark:text-blue-300">
            {config.tip}
          </div>        </div>
      </div>
    </div>
  );
};
